import styled from 'styled-components';
import React from 'react';
import {
  EfficiencyData,
  EfficiencyTranslations,
} from '@volkswagen-onehub/whitelabel-e-mobility-data-types';
import {ExternallyRenderedDisclaimerReference} from './externally-rendered-disclaimer-reference';
import {useComponent} from '../component-library';

export interface EmissionConsumptionDefinitionListProps {
  readonly efficiencyData: EfficiencyData;
  readonly efficiencyTranslations: EfficiencyTranslations;
  readonly disclaimerReferenceLabelTranslation: string | undefined;
  readonly closeButtonLabel: string;
  readonly isEfficiencyClassMandatory?: boolean;
}

export function EmissionConsumptionDefinitionList({
  efficiencyData,
  efficiencyTranslations,
  disclaimerReferenceLabelTranslation,
  closeButtonLabel,
  isEfficiencyClassMandatory,
}: EmissionConsumptionDefinitionListProps): JSX.Element | null {
  const {consumptionsEmissions, efficiencyClass, electricRanges} =
    efficiencyData.combinedEfficiencyData;

  const {
    combinedEfficienciesAriaLabel,
    consumptionEmissionDisclaimers,
    consumptionEmissionDisclaimer: configConsumptionEmissionDisclaimer,
    efficiencyClassDisclaimers,
    efficiencyClassDisclaimer: configEfficiencyClassDisclaimer,
    electricRangeDisclaimer,
  } = efficiencyTranslations;

  const consumptionEmissionDisclaimer = configConsumptionEmissionDisclaimer
    ? configConsumptionEmissionDisclaimer
    : consumptionEmissionDisclaimers
    ? consumptionEmissionDisclaimers['WLTP']
    : undefined;

  const efficiencyClassDisclaimer = configEfficiencyClassDisclaimer
    ? configEfficiencyClassDisclaimer
    : efficiencyClassDisclaimers
    ? efficiencyClassDisclaimers['WLTP']
    : undefined;

  const Text = useComponent('Text');

  if (
    !consumptionsEmissions.some((e) => e.consumptions || e.emissions) &&
    !efficiencyClass
  ) {
    return null;
  }

  return (
    <StyledDefinitionList aria-label={combinedEfficienciesAriaLabel}>
      {consumptionsEmissions.map(({consumptions, emissions}) => [
        consumptions && (
          <React.Fragment key={consumptions.label}>
            <StyledDefinitionTerm>
              <Text tag="span" variant="copy-medium">
                {consumptions.label}
              </Text>
            </StyledDefinitionTerm>
            <StyledDefinitionDescription>
              <Text tag="span" variant="copy-medium">
                {consumptions.value}
              </Text>
              {consumptionEmissionDisclaimer && (
                <ExternallyRenderedDisclaimerReference
                  text={consumptionEmissionDisclaimer}
                  closeButtonLabel={closeButtonLabel}
                  disclaimerReferenceLabelTranslation={
                    disclaimerReferenceLabelTranslation
                  }
                />
              )}
            </StyledDefinitionDescription>
          </React.Fragment>
        ),
        emissions && (
          <React.Fragment key={emissions.label}>
            <StyledDefinitionTerm>
              <Text tag="span" variant="copy-medium">
                {emissions.label}
              </Text>
            </StyledDefinitionTerm>
            <StyledDefinitionDescription>
              <Text tag="span" variant="copy-medium">
                {emissions.value}
              </Text>
              {consumptionEmissionDisclaimer && (
                <ExternallyRenderedDisclaimerReference
                  closeButtonLabel={closeButtonLabel}
                  text={consumptionEmissionDisclaimer}
                  disclaimerReferenceLabelTranslation={
                    disclaimerReferenceLabelTranslation
                  }
                />
              )}
            </StyledDefinitionDescription>
          </React.Fragment>
        ),
      ])}
      {efficiencyClass && isEfficiencyClassMandatory && (
        <React.Fragment key={efficiencyClass.label}>
          <StyledDefinitionTerm>
            <Text tag="span" variant="copy-medium">
              {efficiencyClass.label}
            </Text>
          </StyledDefinitionTerm>
          <StyledDefinitionDescription>
            <Text tag="span" variant="copy-medium">
              {efficiencyClass.value}
            </Text>
            {efficiencyClassDisclaimer && (
              <ExternallyRenderedDisclaimerReference
                closeButtonLabel={closeButtonLabel}
                text={efficiencyClassDisclaimer}
                disclaimerReferenceLabelTranslation={
                  disclaimerReferenceLabelTranslation
                }
              />
            )}
          </StyledDefinitionDescription>
        </React.Fragment>
      )}
      {electricRanges && electricRanges.label && (
        <React.Fragment key={electricRanges.label}>
          <StyledDefinitionTerm>
            <Text tag="span" variant="copy-medium">
              {electricRanges.label}
            </Text>
          </StyledDefinitionTerm>
          <StyledDefinitionDescription>
            <Text tag="span" variant="copy-medium">
              {electricRanges.value}
            </Text>
            {electricRangeDisclaimer && (
              <ExternallyRenderedDisclaimerReference
                closeButtonLabel={closeButtonLabel}
                text={electricRangeDisclaimer}
                disclaimerReferenceLabelTranslation={
                  disclaimerReferenceLabelTranslation
                }
              />
            )}
          </StyledDefinitionDescription>
        </React.Fragment>
      )}
    </StyledDefinitionList>
  );
}

const StyledDefinitionList = styled.dl`
  margin: 0;
`;

const StyledDefinitionTerm = styled.dt`
  display: inline;

  &:not(:last-child)::after {
    content: ' ';
  }
`;

const StyledDefinitionDescription = styled.dd`
  display: inline;
  margin: 0;

  &:not(:last-child)::after {
    content: '${(props) => props.theme.brand.elements.efficiencyDataSeparator}';
  }
`;
